import React, { useEffect, useState } from 'react';
import useTablePusher from './PusherHandler.js';

/**
 * Example Component demonstrating the Singleton Pusher Hook
 * 
 * This example shows how multiple components can use the same
 * Pusher connection without creating duplicate connections.
 * 
 * Key Benefits Demonstrated:
 * - Single connection shared across components
 * - No reconnections when navigating between pages
 * - Unified connection state management
 */
const TablePusherExample = () => {
    const [notifications, setNotifications] = useState([]);
    const [customEvents, setCustomEvents] = useState([]);

    const {
        connectionStatus,
        lastUpdate,
        error,
        addEventHandler,
        removeEventHandler,
        reconnect,
        sendMessage,
        isConnected
    } = useTablePusher();

    // Add custom event handlers when component mounts
    useEffect(() => {
        // Custom event for order updates
        addEventHandler('order-update', (data) => {
            console.log('Order Update received:', data);
            setCustomEvents(prev => [...prev, {
                id: Date.now(),
                type: 'order-update',
                data,
                timestamp: new Date().toLocaleTimeString()
            }]);
        });

        // Custom event for staff notifications
        addEventHandler('staff-notification', (data) => {
            console.log('Staff notification received:', data);
            setNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'staff-notification',
                message: data.message || 'New staff notification',
                timestamp: new Date().toLocaleTimeString(),
                priority: data.priority || 'normal'
            }]);
        });

        // Cleanup function to remove handlers when component unmounts
        return () => {
            removeEventHandler('order-update');
            removeEventHandler('staff-notification');
        };
    }, [addEventHandler, removeEventHandler]);

    // Handle last update changes
    useEffect(() => {
        if (lastUpdate) {
            console.log('New update received:', lastUpdate);
        }
    }, [lastUpdate]);

    // Function to clear notifications
    const clearNotifications = () => {
        setNotifications([]);
    };

    // Function to clear custom events
    const clearCustomEvents = () => {
        setCustomEvents([]);
    };

    // Function to send a test message (for testing purposes)
    const sendTestMessage = () => {
        const success = sendMessage('test-event', {
            message: 'Test message from client',
            timestamp: Date.now()
        });
        
        if (!success) {
            alert('Failed to send message. Make sure you are connected.');
        }
    };

    // Get status indicator color
    const getStatusColor = (status) => {
        switch (status) {
            case 'connected': return 'text-green-500';
            case 'connecting': return 'text-yellow-500';
            case 'disconnected': return 'text-gray-500';
            case 'error': return 'text-red-500';
            default: return 'text-gray-500';
        }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Table Pusher Example</h1>

            {/* Connection Status */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <h2 className="text-lg font-semibold mb-3">Connection Status</h2>
                <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium">Status:</span>
                    <span className={`font-semibold ${getStatusColor(connectionStatus)}`}>
                        {connectionStatus.toUpperCase()}
                    </span>
                    {connectionStatus === 'connected' && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            Connected ✓
                        </span>
                    )}
                </div>
                
                {error && (
                    <div className="mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        <strong>Error:</strong> {error}
                    </div>
                )}
                
                <div className="mt-4 space-x-2">
                    <button
                        onClick={reconnect}
                        disabled={connectionStatus === 'connected'}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        Reconnect
                    </button>
                    <button
                        onClick={sendTestMessage}
                        disabled={!isConnected}
                        className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        Send Test Message
                    </button>
                </div>
            </div>

            {/* Last Update Display */}
            {lastUpdate && (
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                    <h2 className="text-lg font-semibold mb-3">Last Update</h2>
                    <div className="bg-gray-50 rounded p-3">
                        <div className="text-sm text-gray-600">
                            <strong>Type:</strong> {lastUpdate.type}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                            <strong>Time:</strong> {new Date(lastUpdate.timestamp).toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                            <strong>Data:</strong>
                        </div>
                        <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                            {JSON.stringify(lastUpdate.data, null, 2)}
                        </pre>
                    </div>
                </div>
            )}

            {/* Notifications */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold">Staff Notifications</h2>
                    <button
                        onClick={clearNotifications}
                        className="text-sm px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                        Clear All
                    </button>
                </div>
                
                {notifications.length === 0 ? (
                    <p className="text-gray-500 text-sm">No notifications received yet.</p>
                ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`p-3 rounded border-l-4 ${
                                    notification.priority === 'high' 
                                        ? 'border-red-500 bg-red-50' 
                                        : 'border-blue-500 bg-blue-50'
                                }`}
                            >
                                <div className="text-sm font-medium">
                                    {notification.message}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                    {notification.timestamp} • Priority: {notification.priority}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Custom Events */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold">Custom Events</h2>
                    <button
                        onClick={clearCustomEvents}
                        className="text-sm px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                        Clear All
                    </button>
                </div>
                
                {customEvents.length === 0 ? (
                    <p className="text-gray-500 text-sm">No custom events received yet.</p>
                ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {customEvents.map((event) => (
                            <div key={event.id} className="p-3 bg-gray-50 rounded">
                                <div className="flex justify-between items-start">
                                    <div className="text-sm font-medium text-purple-700">
                                        {event.type}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {event.timestamp}
                                    </div>
                                </div>
                                <pre className="text-xs bg-white p-2 rounded mt-2 overflow-x-auto">
                                    {JSON.stringify(event.data, null, 2)}
                                </pre>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Usage Instructions */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Singleton Pattern Benefits</h3>
                <div className="text-sm text-blue-700 space-y-1">
                    <p>✅ <strong>Single Connection:</strong> One WebSocket connection shared across all components</p>
                    <p>✅ <strong>No Reconnections:</strong> Navigating between pages maintains the same connection</p>
                    <p>✅ <strong>Better Performance:</strong> Reduced memory usage and network overhead</p>
                    <p>✅ <strong>Unified State:</strong> All components see the same connection status and events</p>
                </div>
            </div>

            {/* Implementation Guide */}
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-lg font-semibold text-green-800 mb-2">How to Use</h3>
                <div className="text-sm text-green-700 space-y-1">
                    <p>1. Initialize Pusher once in your App.jsx or main.jsx</p>
                    <p>2. Use useTablePusher() hook in any component</p>
                    <p>3. All components automatically share the same connection</p>
                    <p>4. Add custom event handlers as needed</p>
                    <p>5. Monitor connection status and handle errors gracefully</p>
                </div>
            </div>

            {/* Multiple Component Demo */}
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-800 mb-2">Multiple Components Example</h3>
                <div className="text-sm text-purple-700">
                    <p className="mb-2">Try opening this component in multiple places - they all share the same connection!</p>
                    <Sub1 />
                    <Sub2 />
                </div>
            </div>
        </div>
    );
};

// Demonstration sub-components
const Sub1 = () => {
    const { connectionStatus, isConnected } = useTablePusher();
    return (
        <div className="mt-2 p-2 bg-white rounded border">
            <strong>Sub-Component 1:</strong> Status: <span className="text-purple-600">{connectionStatus}</span>
            {isConnected ? ' 🟢' : ' 🔴'}
        </div>
    );
};

const Sub2 = () => {
    const { connectionStatus, isConnected } = useTablePusher();
    return (
        <div className="mt-2 p-2 bg-white rounded border">
            <strong>Sub-Component 2:</strong> Status: <span className="text-purple-600">{connectionStatus}</span>
            {isConnected ? ' 🟢' : ' 🔴'}
        </div>
    );
};

export default TablePusherExample;
